import React, { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { ChevronLeft, ChevronRight, Close, Visibility } from "@mui/icons-material";

const resolveImageUrl = (value) => {
  if (!value) return "";
  if (/^(?:https?:|data:|blob:)/i.test(value)) return value;
  return `${process.env.REACT_APP_API_BASE_URL || ""}${value}`;
};

const GallerySection = ({ images = [], albums = [], loading }) => {
  const [page, setPage] = useState(0);
  const [open, setOpen] = useState(false);
  const [activeAlbumIndex, setActiveAlbumIndex] = useState(0);
  const [activeImageIndex, setActiveImageIndex] = useState(0);

  const galleryAlbums = useMemo(() => {
    const publishedAlbums = albums
      .filter((album) => Array.isArray(album.images) && album.images.length)
      .map((album) => ({
        ...album,
        images: album.images.map((image) => ({
          ...image,
          image: resolveImageUrl(image.image),
        })),
      }));
    const legacyAlbums = images
      .filter((image) => image?.image)
      .map((image, index) => ({
        id: image.id || `legacy-${index}`,
        title: image.title || "Community moment",
        description: "",
        images: [{ ...image, image: resolveImageUrl(image.image) }],
      }));
    return [...publishedAlbums, ...legacyAlbums];
  }, [albums, images]);

  const itemsPerPage = 6;
  const totalPages = Math.ceil(galleryAlbums.length / itemsPerPage);
  const start = page * itemsPerPage;
  const currentAlbums = galleryAlbums.slice(start, start + itemsPerPage);
  const activeAlbum = galleryAlbums[activeAlbumIndex];
  const activeImages = activeAlbum?.images || [];

  const handleOpen = (index) => {
    setActiveAlbumIndex(start + index);
    setActiveImageIndex(0);
    setOpen(true);
  };

  const handlePrev = () => {
    setActiveImageIndex((prev) => (prev === 0 ? activeImages.length - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveImageIndex((prev) => (prev === activeImages.length - 1 ? 0 : prev + 1));
  };

  return (
    <motion.article
      className="gallery-card ne-card"
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45 }}
      viewport={{ once: true }}
    >
      <div className="gallery-header card-header">
        <span className="section-kicker">Gallery</span>
        <h3>Community Moments</h3>
      </div>

      <div className="gallery-grid premium">
        {loading &&
          Array.from({ length: 6 }).map((_, i) => (
            <div className="gallery-item premium ne-skeleton" key={i} />
          ))}

        {!loading && currentAlbums.length === 0 && (
          <div className="home-empty-state">Gallery images will appear here soon.</div>
        )}

        {!loading &&
          currentAlbums.map((album, i) => (
            <motion.button
              key={album.id || `${album.title}-${i}`}
              type="button"
              className="gallery-item premium"
              whileHover={{ y: -6 }}
              onClick={() => handleOpen(i)}
            >
              <span className="gallery-image-wrap">
                <img
                  src={album.images[0]?.image}
                  alt={album.title || "Community moment"}
                />

                <span className="gallery-overlay">
                  <Visibility fontSize="small" />
                  View {album.images.length > 1 ? `${album.images.length} photos` : ""}
                </span>
              </span>

              <span className="gallery-title">{album.title || "Community moment"}</span>
              {album.description && (
                <span className="gallery-summary">{album.description}</span>
              )}
            </motion.button>
          ))}
      </div>

      {totalPages > 1 && (
        <div className="gallery-controls">
          <button
            type="button"
            aria-label="Previous gallery page"
            onClick={() => setPage((p) => Math.max(p - 1, 0))}
            disabled={page === 0}
          >
            <ChevronLeft fontSize="small" />
          </button>

          <span>
            {page + 1} / {totalPages}
          </span>

          <button
            type="button"
            aria-label="Next gallery page"
            onClick={() => setPage((p) => Math.min(p + 1, totalPages - 1))}
            disabled={page === totalPages - 1}
          >
            <ChevronRight fontSize="small" />
          </button>
        </div>
      )}

      {open && activeAlbum && activeImages[activeImageIndex] && (
        <div className="gallery-modal" role="dialog" aria-modal="true">
          <button
            className="gallery-close"
            type="button"
            aria-label="Close gallery"
            onClick={() => setOpen(false)}
          >
            <Close />
          </button>

          {activeImages.length > 1 && (
            <button className="gallery-nav left" type="button" onClick={handlePrev}>
              <ChevronLeft />
            </button>
          )}

          <div className="gallery-modal-content">
            <img
              src={activeImages[activeImageIndex]?.image}
              alt={activeImages[activeImageIndex]?.alt || activeAlbum.title || "Gallery preview"}
              className="gallery-modal-img"
            />
            <div className="gallery-modal-copy">
              <strong>{activeAlbum.title}</strong>
              {activeAlbum.description && <p>{activeAlbum.description}</p>}
              {activeImages.length > 1 && (
                <span>{activeImageIndex + 1} of {activeImages.length}</span>
              )}
            </div>
          </div>

          {activeImages.length > 1 && (
            <button className="gallery-nav right" type="button" onClick={handleNext}>
              <ChevronRight />
            </button>
          )}
        </div>
      )}
    </motion.article>
  );
};

export default GallerySection;
